import { PageHeaderLayout, RouteTabWithContent, TabContent } from "@homebound/beam";
import { useMemo } from "react";
import { Navigate } from "react-router-dom";
import { createPlanPackageDetailsOverviewUrl, createPlanPackagesUrl } from "src/RouteUrls";
import { Loading } from "src/components/Loading";
import { useFeatureFlag } from "src/contexts/FeatureFlags/FeatureFlagContext";
import { useUserFeatureFlagsQuery } from "src/contexts/FeatureFlags/FeatureFlagContext.generated";
import { FeatureFlagType } from "src/generated/graphqlTypes";
import { useRouteParams } from "src/hooks/useRouteParams";
import {
  PlanPackagePageV2MetadataQuery,
  usePlanPackagePageV2MetadataQuery,
} from "src/routes/libraries/plan-package-v2/PlanPackagePageV2.generated";
import { PlanPackageDetailsParams } from "src/routes/routesDef";
import {
  planPackageCheckoutRoute,
  planPackageDocumentsRoute,
  planPackageElevationsRoute,
  planPackageLocationsRoute,
  planPackageOptionsRoute,
  planPackageOverviewRoute,
  planPackageProgramDataRoute,
  planPackageRoute,
  planPackageTakeoffsRoute,
} from "src/routing/domains/librariesPaths";
import { queryResult } from "src/utils";

export function PlanPackagePageV2() {
  const isPlanPackagesV2Enabled = useFeatureFlag(FeatureFlagType.PlanPackagesV2);
  const { loading: flagsLoading } = useUserFeatureFlagsQuery();
  const { planPackageId, planPackageVersionId } = useRouteParams<PlanPackageDetailsParams>();
  const query = usePlanPackagePageV2MetadataQuery({
    variables: { id: planPackageId, versionId: planPackageVersionId },
    skip: !isPlanPackagesV2Enabled,
  });
  if (flagsLoading) return <Loading />;
  if (!isPlanPackagesV2Enabled) {
    return <Navigate to={createPlanPackageDetailsOverviewUrl(planPackageId, planPackageVersionId)} replace />;
  }
  return queryResult(query, ({ planPackage }) => <DataView planPackage={planPackage} />);
}

type DataViewProps = { planPackage: PlanPackagePageV2MetadataQuery["planPackage"] };

function DataView({ planPackage }: DataViewProps) {
  const { planPackageId, planPackageVersionId } = useRouteParams<PlanPackageDetailsParams>();
  const tabs: RouteTabWithContent[] = useMemo(() => {
    const params = { planPackageId, planPackageVersionId };
    return [
      {
        name: "Overview",
        href: planPackageOverviewRoute.url(params),
        path: [planPackageRoute.path, planPackageOverviewRoute.path],
        render: () => <></>,
      },
      {
        name: "Elevations",
        href: planPackageElevationsRoute.url(params),
        path: planPackageElevationsRoute.path,
        render: () => <></>,
      },
      {
        name: "Program Data",
        href: planPackageProgramDataRoute.url(params),
        path: planPackageProgramDataRoute.path,
        render: () => <></>,
      },
      {
        name: "Locations",
        href: planPackageLocationsRoute.url(params),
        path: planPackageLocationsRoute.path,
        render: () => <></>,
      },
      {
        name: "Options",
        href: planPackageOptionsRoute.url(params),
        path: planPackageOptionsRoute.path,
        render: () => <></>,
      },
      {
        name: "Takeoffs",
        href: planPackageTakeoffsRoute.url(params),
        path: planPackageTakeoffsRoute.path,
        render: () => <></>,
      },
      {
        name: "Checkout",
        href: planPackageCheckoutRoute.url(params),
        path: planPackageCheckoutRoute.path,
        render: () => <></>,
      },
      {
        name: "Documents",
        href: planPackageDocumentsRoute.url(params),
        path: planPackageDocumentsRoute.path,
        render: () => <></>,
      },
    ];
  }, [planPackageId, planPackageVersionId]);

  return (
    <PageHeaderLayout
      pageHeader={{
        title: planPackage.name,
        breadcrumbs: { breadcrumbs: { label: "Plan Library", href: createPlanPackagesUrl() } },
        tabs: { tabs },
      }}
    >
      <TabContent tabs={tabs} />
    </PageHeaderLayout>
  );
}
